import { defineStore } from 'pinia'
import { ref, computed, onScopeDispose } from 'vue'
import { apiService } from '@/services/api/index.js'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const initialized = ref(false)
  let _initializePromise = null // In-flight guard: prevents parallel /users/me calls

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => !!user.value && user.value.is_admin === true)
  const userInitials = computed(() => {
    if (!user.value) return ''
    const name = user.value.name || user.value.email || ''
    const nameParts = name.split(' ')
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  })

  const initialize = async () => {
    if (initialized.value) return

    // Reuse in-flight promise if initialization is already in progress
    if (_initializePromise) return _initializePromise

    _initializePromise = (async () => {
      try {
        // Cookie is sent automatically — if valid, this succeeds and sets user
        await loadUser()
      } catch {
        user.value = null
      } finally {
        initialized.value = true
        _initializePromise = null
      }
    })()

    return _initializePromise
  }

  const login = async (credentials) => {
    isLoading.value = true
    error.value = null

    try {
      await apiService.login(credentials)

      // Cookie is now set by the backend — load user to confirm session
      await loadUser()

      return { success: true }
    } catch (err) {
      // Handle both transformed errors (from handleApiError) and raw axios errors
      error.value = err.response?.data?.detail || err.message || 'Login failed'
      return {
        success: false,
        error: error.value
      }
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData) => {
    isLoading.value = true
    error.value = null

    try {
      // Registration returns a message only — no token.
      // The user must verify their email before they can log in.
      await apiService.register(userData)
      return { success: true }
    } catch (err) {
      // Handle both transformed errors (from handleApiError) and raw axios errors
      error.value = err.response?.data?.detail || err.message || 'Registration failed'
      return {
        success: false,
        error: error.value
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    // Already logged out — nothing to do (avoids noise from auth:unauthorized during startup)
    if (!user.value && !isLoading.value) return

    isLoading.value = true

    try {
      await apiService.logout()
    } catch (err) {
      // Best-effort — cookie will expire naturally if request fails
    } finally {
      user.value = null
      error.value = null
      isLoading.value = false
    }
  }

  const loadUser = async () => {
    try {
      user.value = await apiService.getCurrentUser()
    } catch (err) {
      // 401 during startup is expected (not logged in) — don't noise the console
      const status = err.response?.status || err.status
      if (status !== 401) {
        console.error('Failed to load user:', err)
      }
      throw err
    }
  }

  const forgotPassword = async (email) => {
    isLoading.value = true
    error.value = null

    try {
      await apiService.forgotPassword(email)
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.detail || 'Failed to send reset email'
      return { 
        success: false, 
        error: error.value 
      }
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (token, newPassword) => {
    isLoading.value = true
    error.value = null

    try {
      await apiService.resetPassword(token, newPassword)
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.detail || 'Failed to reset password'
      return { 
        success: false, 
        error: error.value 
      }
    } finally {
      isLoading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  // Listen for auth events from the API client interceptor
  if (typeof window !== 'undefined') {
    const handleUnauthorized = () => logout()
    window.addEventListener('auth:unauthorized', handleUnauthorized)
    onScopeDispose(() => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
    })
  }

  return {
    user,
    isLoading,
    error,
    initialized,

    isAuthenticated,
    isAdmin,
    userInitials,

    initialize,
    login,
    register,
    logout,
    loadUser,
    forgotPassword,
    resetPassword,
    clearError
  }
})